import * as React from "react";
import type { ColumnDef } from "@tanstack/react-table";

import type { AuditLogEntry } from "api/auditLogs";
import { Avatar, Badge, IconButton, middleTruncate } from "Components/ui";
import { ChevronRight, Workflow } from "Components/ui/icons";
import { formatDateTime, formatRelative } from "utils/format";
import { formatActionLabel, formatSource, getActionMeta, isSystemEntry } from "./actionMeta";

const SecondaryId: React.FC<{ value: string }> = ({ value }) => (
  <div className="truncate font-mono text-xs text-muted-foreground" title={value}>
    {middleTruncate(value)}
  </div>
);

/** Columns for the audit log table; `onOpen` opens an entry's detail sheet. */
export const buildAuditLogColumns = (
  onOpen: (entry: AuditLogEntry) => void
): ColumnDef<AuditLogEntry>[] => [
  {
    id: "time",
    header: "Time",
    meta: { className: "w-48 whitespace-nowrap py-2.5" },
    cell: ({ row }) => {
      const absolute = formatDateTime(row.original.timestamp);
      return (
        <div title={absolute}>
          <div className="text-foreground">{formatRelative(row.original.timestamp)}</div>
          <div className="text-xs tabular-nums text-muted-foreground">{absolute}</div>
        </div>
      );
    },
  },
  {
    id: "actor",
    header: "Actor",
    meta: { className: "min-w-[12rem] py-2.5" },
    cell: ({ row }) => {
      const { actorName, actorId, source } = row.original;
      if (isSystemEntry(row.original)) {
        return (
          <div className="flex min-w-0 items-center gap-2.5">
            <span
              aria-hidden="true"
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-muted text-muted-foreground [&_svg]:h-4 [&_svg]:w-4"
            >
              <Workflow />
            </span>
            <div className="min-w-0">
              <div className="truncate font-medium text-foreground">System</div>
              <div className="truncate text-xs text-muted-foreground">{formatSource(source)}</div>
            </div>
          </div>
        );
      }
      const label = actorName || actorId || "Unknown";
      return (
        <div className="flex min-w-0 items-center gap-2.5">
          <Avatar name={label} size="md" />
          <div className="min-w-0">
            <div className="truncate font-medium text-foreground">{label}</div>
            {actorName && actorId && <SecondaryId value={actorId} />}
          </div>
        </div>
      );
    },
  },
  {
    id: "action",
    header: "Action",
    meta: { className: "py-2.5" },
    cell: ({ row }) => {
      const { action } = row.original;
      return (
        <Badge tone={getActionMeta(action).tone} dot title={action}>
          {formatActionLabel(action)}
        </Badge>
      );
    },
  },
  {
    id: "target",
    header: "Target",
    meta: { className: "min-w-[12rem] py-2.5" },
    cell: ({ row }) => {
      const { targetName, targetId, targetTenant } = row.original;
      return (
        <div className="min-w-0">
          {targetName ? (
            <div className="truncate font-medium text-foreground">{targetName}</div>
          ) : (
            !targetId && <div className="text-subtle-foreground">—</div>
          )}
          {targetTenant ? (
            // Billing targets: the client they belong to, with its pm2 app.
            <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
              <span className="truncate">{targetTenant.name}</span>
              {targetTenant.slug && (
                <Badge className="shrink-0 font-mono" title="pm2 app">
                  {targetTenant.slug}
                </Badge>
              )}
            </div>
          ) : (
            targetId && <SecondaryId value={targetId} />
          )}
        </div>
      );
    },
  },
  {
    id: "open",
    header: () => <span className="sr-only">Details</span>,
    meta: { align: "right", className: "w-14" },
    cell: ({ row }) => (
      <IconButton
        label="View details"
        icon={<ChevronRight aria-hidden="true" />}
        size="sm"
        className="text-muted-foreground"
        onClick={() => onOpen(row.original)}
      />
    ),
  },
];
